import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab, Switch, FormControlLabel, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useSnackbar } from 'notistack';
import sucVentaService, { SucVenta } from '../../services/sucVentaService';
import sucursalesVentaService, { SucursalVenta } from '../../services/sucursalesVentaService';
import tipoFormularioService, { TipoFormulario, TIPO_FORMULARIO_MAX_PAGE_SIZE } from '../../services/tipoFormularioService';
import wsfeConfigService, { WsfeConfigItem } from '../../services/wsfeConfigService';

const defaultForm: SucVenta = {
  nroSuc: 0,
  nroTipoFormulario: '',
  formuA: 0,
  formuB: 0,
  formuC: 0,
  formuN: 0,
  numPropio: 'N',
  nroTipoFormularioNumerador: '',
  copias: 1,
  renglones: 10,
  plantiA: '',
  plantiB: '',
  plantiC: '',
  plantiN: '',
  plantiComun: '',
  impA: '',
  impB: '',
  impC: '',
  impN: '',
  impComun: '',
  vpreviaA: 'N',
  vpreviaB: 'N',
  vpreviaC: 'N',
  vpreviaN: 'N',
  vpreviaComun: 'N',
  imprime: 'N',
  liquidaComision: 'N',
  afectaLimCred: 'N',
  actualizaLimCred: 'N',
  tipoImpresora: '',
  usaCodigoBarra: 0,
  tipoRenglones: '',
  idWsfe: '',
  generaRemito: 'N',
  nroSucRemito: 0,
  soloAdvertencia: 0,
  usaLetraM: 0,
  idWsfecred: '',
  passEmision: '',
  passAnulacion: '',
  generaOcarga: 'N',
  nroSucOcarga: 0
};

const SucVentaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroSuc, nroTipoFormulario } = useParams();
  const [formData, setFormData] = useState<SucVenta>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [tipoFormularios, setTipoFormularios] = useState<TipoFormulario[]>([]);
  const [wsfeConfigs, setWsfeConfigs] = useState<WsfeConfigItem[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const isNew = !nroSuc || !nroTipoFormulario || nroSuc === 'nuevo' || nroTipoFormulario === 'nuevo';

  const loadSucVenta = useCallback(async () => {
    if (!nroSuc || !nroTipoFormulario || nroSuc === 'nuevo' || nroTipoFormulario === 'nuevo') return;
    try {
      setLoading(true);
      const sucVenta = await sucVentaService.getSucVenta(Number(nroSuc), nroTipoFormulario);
      setFormData(sucVenta);
    } catch (error) {
      enqueueSnackbar('Error al cargar la asignación', { variant: 'error' });
      navigate('/configuraciones/sucventa');
    } finally {
      setLoading(false);
    }
  }, [nroSuc, nroTipoFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroSuc && nroSuc !== 'nuevo' && nroTipoFormulario && nroTipoFormulario !== 'nuevo') loadSucVenta();
  }, [nroSuc, nroTipoFormulario, loadSucVenta]);

  useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const [sucRes, tipRes, wsfeRes] = await Promise.all([
          sucursalesVentaService.getSucursalesVenta({ pageNumber: 1, pageSize: 20 }),
          tipoFormularioService.getTipoFormularios({ pageNumber: 1, pageSize: TIPO_FORMULARIO_MAX_PAGE_SIZE }),
          wsfeConfigService.getWsfeConfigs()
        ]);
        setSucursales(sucRes.items || []);
        setTipoFormularios(tipRes.items || []);
        setWsfeConfigs(Array.isArray(wsfeRes) ? wsfeRes : []);
      } catch (err) {
        console.error('Error al cargar opciones:', err);
        enqueueSnackbar('Error al cargar sucursales y tipos de formulario', { variant: 'error' });
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, [enqueueSnackbar]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (['nroSuc', 'formuA', 'formuB', 'formuC', 'formuN', 'copias', 'renglones', 'usaCodigoBarra', 'nroSucRemito', 'soloAdvertencia', 'usaLetraM', 'nroSucOcarga'].includes(name)) {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSwitchChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [name]: e.target.checked ? 'S' : 'N' }));
  };

  const handleSelectSucursal = (e: { target: { value: unknown } }) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, nroSuc: val === '' ? 0 : Number(val) }));
  };

  const handleSelectTipoFormulario = (e: { target: { value: unknown } }) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, nroTipoFormulario: val === '' ? '' : String(val).trim() }));
  };

  const handleSelectSucRemito = (e: { target: { value: unknown } }) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, nroSucRemito: val === '' || val === null ? 0 : Number(val) }));
  };

  const handleSelectSucOcarga = (e: { target: { value: unknown } }) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, nroSucOcarga: val === '' || val === null ? 0 : Number(val) }));
  };

  const handleSelectIdWsfe = (e: { target: { value: unknown } }) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, idWsfe: val === '' || val === null ? '' : String(val).trim() }));
  };

  const handleSelectIdWsfecred = (e: { target: { value: unknown } }) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, idWsfecred: val === '' || val === null ? '' : String(val).trim() }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (isNew) {
        if (!formData.nroSuc) {
          enqueueSnackbar('Seleccione una sucursal de venta', { variant: 'warning' });
          return;
        }
        if (!formData.nroTipoFormulario?.trim()) {
          enqueueSnackbar('Seleccione un tipo de formulario', { variant: 'warning' });
          return;
        }
      }

      const payload = {
        ...formData,
        numPropio: (formData.numPropio || 'N').toUpperCase() === 'S' ? 'S' : 'N'
      };
      if (nroSuc && nroSuc !== 'nuevo' && nroTipoFormulario && nroTipoFormulario !== 'nuevo') {
        await sucVentaService.updateSucVenta(Number(nroSuc), nroTipoFormulario, payload);
      } else {
        await sucVentaService.createSucVenta(payload);
      }

      enqueueSnackbar('Asignación guardada exitosamente', { variant: 'success' });
      navigate('/configuraciones/sucventa');
    } catch (error: any) {
      console.error('Error al guardar asignación:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la asignación', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {nroSuc && nroSuc !== 'nuevo' && nroTipoFormulario && nroTipoFormulario !== 'nuevo' ? 'Editar Asignación' : 'Nueva Asignación'}
          </Typography>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Numeración" />
            <Tab label="Impresión" />
            <Tab label="Configuración" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {isNew ? (
                    <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                      <InputLabel>Sucursal de Venta</InputLabel>
                      <Select
                        value={formData.nroSuc || ''}
                        label="Sucursal de Venta"
                        onChange={handleSelectSucursal}
                        disabled={loadingOptions}
                      >
                        <MenuItem value="">
                          <em>Seleccione sucursal</em>
                        </MenuItem>
                        {sucursales.map((s) => (
                          <MenuItem key={s.nroSuc} value={s.nroSuc}>
                            {s.nroSuc} - {s.nombreSucursal}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField 
                      name="nroSuc" 
                      label="Sucursal de Venta" 
                      value={`${formData.nroSuc}${formData.nombreSucursal ? ` - ${formData.nombreSucursal}` : ''}`}
                      fullWidth 
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {isNew ? (
                    <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                      <InputLabel>Tipo de Formulario</InputLabel>
                      <Select
                        value={formData.nroTipoFormulario || ''}
                        label="Tipo de Formulario"
                        onChange={handleSelectTipoFormulario}
                        disabled={loadingOptions}
                      >
                        <MenuItem value="">
                          <em>Seleccione tipo de formulario</em>
                        </MenuItem>
                        {tipoFormularios.map((tf) => (
                          <MenuItem key={tf.nroTipoFormulario} value={String(tf.nroTipoFormulario).trim()}>
                            {tf.nroTipoFormulario} - {tf.descripcion}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField 
                      name="nroTipoFormulario" 
                      label="Tipo de Formulario" 
                      value={`${formData.nroTipoFormulario || ''}${formData.tipoFormulario?.descripcion ? ` - ${formData.tipoFormulario.descripcion.trim()}` : ''}`}
                      fullWidth 
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="copias" 
                    label="Copias" 
                    type="number"
                    value={formData.copias || 1} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="renglones" 
                    label="Renglones" 
                    type="number"
                    value={formData.renglones || 10} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.imprime === 'S'} onChange={handleSwitchChange('imprime')} />}
                    label="Imprime"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.liquidaComision === 'S'} onChange={handleSwitchChange('liquidaComision')} />}
                    label="Liquida Comisión"
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="formuA" 
                    label="Formulario A" 
                    type="number"
                    value={formData.formuA || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="formuB" 
                    label="Formulario B" 
                    type="number"
                    value={formData.formuB || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="formuC" 
                    label="Formulario C" 
                    type="number"
                    value={formData.formuC || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="formuN" 
                    label="Formulario N" 
                    type="number"
                    value={formData.formuN || 0} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={(formData.numPropio || 'N') === 'S'} 
                        onChange={handleSwitchChange('numPropio')} 
                      />
                    }
                    label="Impresión propia"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="nroTipoFormularioNumerador" 
                    label="Tipo Form. Numerador" 
                    value={formData.nroTipoFormularioNumerador || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="plantiA" 
                    label="Plantilla A" 
                    value={formData.plantiA || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="impA" 
                    label="Impresora A" 
                    value={formData.impA || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="plantiB" 
                    label="Plantilla B" 
                    value={formData.plantiB || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="impB" 
                    label="Impresora B" 
                    value={formData.impB || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="plantiC" 
                    label="Plantilla C" 
                    value={formData.plantiC || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="impC" 
                    label="Impresora C" 
                    value={formData.impC || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="plantiN" 
                    label="Plantilla N" 
                    value={formData.plantiN || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="impN" 
                    label="Impresora N" 
                    value={formData.impN || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="tipoImpresora" 
                    label="Tipo Impresora" 
                    value={formData.tipoImpresora || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.usaCodigoBarra === 1} onChange={(e) => setFormData(prev => ({ ...prev, usaCodigoBarra: e.target.checked ? 1 : 0 }))} />}
                    label="Usa Código de Barras"
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.afectaLimCred === 'S'} onChange={handleSwitchChange('afectaLimCred')} />}
                    label="Afecta Límite de Crédito"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.actualizaLimCred === 'S'} onChange={handleSwitchChange('actualizaLimCred')} />}
                    label="Actualiza Límite de Crédito"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.generaRemito === 'S'} onChange={handleSwitchChange('generaRemito')} />}
                    label="Genera Remito"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                    <InputLabel>N° Suc. Remito</InputLabel>
                    <Select
                      value={formData.nroSucRemito != null && formData.nroSucRemito !== 0 ? formData.nroSucRemito : ''}
                      label="N° Suc. Remito"
                      onChange={handleSelectSucRemito}
                      disabled={loadingOptions}
                    >
                      <MenuItem value="">
                        <em>Ninguna</em>
                      </MenuItem>
                      {sucursales.map((s) => (
                        <MenuItem key={s.nroSuc} value={s.nroSuc}>
                          {s.nroSuc} - {s.nombreSucursal}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.generaOcarga === 'S'} onChange={handleSwitchChange('generaOcarga')} />}
                    label="Genera Orden de Carga"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                    <InputLabel>N° Suc. O. Carga</InputLabel>
                    <Select
                      value={formData.nroSucOcarga != null && formData.nroSucOcarga !== 0 ? formData.nroSucOcarga : ''}
                      label="N° Suc. O. Carga"
                      onChange={handleSelectSucOcarga}
                      disabled={loadingOptions}
                    >
                      <MenuItem value="">
                        <em>Ninguna</em>
                      </MenuItem>
                      {sucursales.map((s) => (
                        <MenuItem key={s.nroSuc} value={s.nroSuc}>
                          {s.nroSuc} - {s.nombreSucursal}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                    <InputLabel>ID WSFE</InputLabel>
                    <Select
                      value={(formData.idWsfe || '').trim()}
                      label="ID WSFE"
                      onChange={handleSelectIdWsfe}
                      disabled={loadingOptions}
                    >
                      <MenuItem value="">
                        <em>Ninguno</em>
                      </MenuItem>
                      {wsfeConfigs.map((c) => (
                        <MenuItem key={c.idwsfe} value={c.idwsfe}>
                          {c.idwsfe}{c.descripcion ? ` - ${c.descripcion}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                    <InputLabel>ID WSFE Cred</InputLabel>
                    <Select
                      value={(formData.idWsfecred || '').trim()}
                      label="ID WSFE Cred"
                      onChange={handleSelectIdWsfecred}
                      disabled={loadingOptions}
                    >
                      <MenuItem value="">
                        <em>Ninguno</em>
                      </MenuItem>
                      {wsfeConfigs.map((c) => (
                        <MenuItem key={c.idwsfe} value={c.idwsfe}>
                          {c.idwsfe}{c.descripcion ? ` - ${c.descripcion}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/configuraciones/sucventa')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SucVentaForm;
